// UpdateVariantForm.tsx
import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SubmitButton from '../../../form/SubmitButton';
import InputField from '../../../form/InputField';
import SingleImageUploader from '../../../form/SingleImageUploader';
import { HexColorPicker } from 'react-colorful';
import { useVariantCategories } from '../../../../hooks/store/variantCategories';
import { useUpdateResource } from '../../../../hooks/useUpdateResource';
import LoadingButton from '../../../form/LoadingButton';

export type Language = {
  languageCode: string;
  name: string;
};

export type Variant = {
  additional_price: number;
  categoryId?: number;
  languages: Language[];
  image?: string | File | null;
};

export type FormValues = Variant;

interface Props {
  initialValues: Variant;
  id:number,
  onSubmitSuccess?: () => void;
  storeId?:number
}

const UpdateVariantForm: React.FC<Props> = ({ initialValues, onSubmitSuccess,id,storeId }) => {
  const { t } = useTranslation();
  const {data:variantCategories} = useVariantCategories();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    getValues,
  } = useForm<FormValues>({
    defaultValues: initialValues,
  });

  const [image, setImage] = useState<File | string | null>(initialValues.image ?? null);
  const [colorValue, setColorValue] = useState(initialValues.languages[0]?.name || '#000000');

  useEffect(() => {
    reset(initialValues);
    setImage(initialValues.image ?? null);
    setColorValue(initialValues.languages[0]?.name || '#000000');
  }, [initialValues, reset]);

  const selectedCategory = variantCategories?.find(
      cat => cat.id === Number(getValues('categoryId'))
  );
  const isColorCategory = selectedCategory?.languages.find(lng=>lng.name.toLowerCase()==="color"||lng.name==="اللون");


  const {loading,updateResource} = useUpdateResource();

  const onSubmit: SubmitHandler<FormValues> = async(data) => {
    if (!data.categoryId) {
      setError('categoryId', { type: 'required', message: t('required_field') });
      return;
    }
    const formData = new FormData();
    formData.append('additionalPrice', String(data.additional_price));
    formData.append("languages",JSON.stringify(data.languages));
    if (image instanceof File) {
      formData.append('image', image);
    }
    const endPoint = storeId ?`product-variant/${id}?storeId=${storeId}` : `product-variant/${id}`
    await  updateResource(endPoint, formData, storeId ? "admin" : "store",{
            method: "PUT",
            successMessage: t("update_success"),
            invalidateQueries:["product-detail"],
          });
        if (onSubmitSuccess) onSubmitSuccess();
  };

  return (
    <div className="max-w-[600px]">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* Display Selected Category */}
        {selectedCategory && (
          <div className="mb-2 px-3 py-1 rounded border bg-primaryButton text-white w-max">
            {selectedCategory.languages[0].name}
          </div>
        )}


        {/* Image */}
        <SingleImageUploader
          label="optional"
          image={image}
          setImage={setImage}
        />

        {/* Languages / Color */}
        {isColorCategory ? (
          <div className="flex flex-col gap-3 mt-4">
          <span className="font-semibold">{t('select_color')}:</span>
          
          <div className="p-4 rounded-lg shadow-md border border-gray-300 bg-white flex gap-4 items-center">
            
            {/* Color Picker */}
            <HexColorPicker
              style={{ height: 100 }}
              color={colorValue}
              onChange={(color) => {
                setColorValue(color);
                reset({
                  ...getValues(),
                  languages: [
                    { languageCode: 'ar', name: color },
                    { languageCode: 'en', name: color },
                  ],
                });
              }}
            />
            
            {/* Color Preview + Hex Input */}
            <div className="flex flex-col items-center gap-2">
              {/* Color Circle Preview */}
              <div
                className="w-8 h-8 rounded-full border border-gray-400"
                style={{ backgroundColor: colorValue }}
              />
              {/* Hex Code Input */}
              <input
                type="text"
                className="w-20 px-2 py-1 border outline-none rounded text-center min-w-[100px]"
                value={colorValue}
                onChange={(e) => {
                  const val = e.target.value;
                  // Optional: simple validation for hex color
                  if (/^#([0-9A-Fa-f]{0,6})$/.test(val)) {
                    setColorValue(val);
                    reset({
                      ...getValues(),
                      languages: [
                        { languageCode: 'ar', name: val },
                        { languageCode: 'en', name: val },
                      ],
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
        ) : (
          <div className="flex gap-4 mt-2">
            <div className="flex-1">
              <InputField
                placeholder={t('name_en')}
                register={register('languages.1.name', { required: t('required_field') })}
                error={errors?.languages?.[1]?.name}
              />
              <input type="hidden" {...register('languages.1.languageCode')} value="en" />
            </div>
            <div className="flex-1">
              <InputField
                placeholder={t('name_ar')}
                register={register('languages.0.name', { required: t('required_field') })}
                error={errors?.languages?.[0]?.name}
              />
              <input type="hidden" {...register('languages.0.languageCode')} value="ar" />
            </div>
          </div>
        )}

        {/* Additional Price */}
        <InputField
          placeholder={t('additional_price')}
          type="number"
          register={register('additional_price', { required: t('required_field'), valueAsNumber: true })}
          error={errors.additional_price}
        />

        {
          loading 
          ?
          <LoadingButton/>
          :
          <SubmitButton label={t('update')} />
        }
      </form>
    </div>
  );
};

export default UpdateVariantForm;
